import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getCachedData, setCachedData } from '@/lib/indexedDB';

export type PlanType = 'individual' | 'team';

export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  plan_type: PlanType;
  max_users: number;
  subscription_status?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  stripe_price_id?: string;
  current_period_end?: string;
  created_at: string;
  updated_at: string;
}

/** Campos devolvidos por `check-subscription` para alinhar o contexto ao que foi gravado na BD. */
export type WorkspaceStripeSyncPatch = Partial<
  Pick<
    Workspace,
    | 'subscription_status'
    | 'stripe_customer_id'
    | 'stripe_subscription_id'
    | 'stripe_price_id'
    | 'current_period_end'
    | 'max_users'
  >
>;

export interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: string;
  created_at: string;
}

/** Snapshot persisted in IndexedDB for modo offline (workspace sem `user` do Supabase). */
export interface OfflineWorkspaceBundle {
  workspace: Workspace;
  allWorkspaces: Workspace[];
  members: WorkspaceMember[];
}

interface WorkspaceContextValue {
  workspace: Workspace | null;
  allWorkspaces: Workspace[];
  members: WorkspaceMember[];
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isLoading: boolean;
  needsOnboarding: boolean;
  needsWorkspaceSelection: boolean;
  planType: PlanType;
  subscriptionStatus: string | null;
  createWorkspace: (planType: PlanType) => Promise<Workspace | null>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  inviteMember: (email: string) => Promise<boolean>;
  removeMember: (memberId: string) => Promise<boolean>;
  reloadWorkspace: () => Promise<void>;
  /** Mescla campos Stripe no workspace ativo / lista (ex.: após check-subscription). */
  mergeWorkspaceStripeSync: (workspaceId: string, patch: WorkspaceStripeSyncPatch) => void;
  /** Recarrega uma linha de `workspaces` por id (fallback se a resposta da edge não trouxer campos). */
  refreshWorkspaceById: (workspaceId: string) => Promise<void>;
  canCreateIndividual: boolean;
  canCreateTeam: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading: authLoading, isOfflineMode, offlineSession } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsWorkspaceSelection, setNeedsWorkspaceSelection] = useState(false);

  const loadWorkspace = useCallback(async () => {
    if (!user?.id) {
      if (isOfflineMode && offlineSession?.userId) {
        try {
          const bundle = await getCachedData<OfflineWorkspaceBundle>(
            `workspace_bundle_${offlineSession.userId}`
          );
          if (bundle?.workspace) {
            setWorkspace(bundle.workspace);
            setAllWorkspaces(bundle.allWorkspaces?.length ? bundle.allWorkspaces : [bundle.workspace]);
            setMembers(bundle.members ?? []);
            setNeedsOnboarding(false);
            setNeedsWorkspaceSelection(false);
          }
        } catch (e) {
          console.error('Failed to restore offline workspace cache:', e);
        }
      }
      setIsLoading(false);
      return;
    }

    try {
      // Get all workspaces the user is a member of
      const { data: membershipData } = await supabase
        .from('workspace_members')
        .select('workspace_id')
        .eq('user_id', user.id);

      if (!membershipData || membershipData.length === 0) {
        // Check profile for onboarding status
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed, workspace_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profile?.onboarding_completed) {
          setNeedsOnboarding(true);
          setIsLoading(false);
          return;
        }

        // If onboarding completed but no memberships, try loading from profile
        if (profile?.workspace_id) {
          const { data: ws } = await supabase
            .from('workspaces')
            .select('*')
            .eq('id', profile.workspace_id)
            .maybeSingle();

          if (ws) {
            const typedWs = ws as Workspace;
            setWorkspace(typedWs);
            setAllWorkspaces([typedWs]);
            setNeedsOnboarding(false);
            setNeedsWorkspaceSelection(false);
            
            const { data: mData } = await supabase
              .from('workspace_members')
              .select('*')
              .eq('workspace_id', ws.id);
            setMembers((mData || []) as WorkspaceMember[]);
            setIsLoading(false);
            return;
          }
        }

        setNeedsOnboarding(true);
        setIsLoading(false);
        return;
      }

      // Load all workspaces
      const wsIds = membershipData.map(m => m.workspace_id);
      const { data: workspacesData } = await supabase
        .from('workspaces')
        .select('*')
        .in('id', wsIds);

      const typedWorkspaces = (workspacesData || []) as Workspace[];
      setAllWorkspaces(typedWorkspaces);

      if (typedWorkspaces.length === 0) {
        setNeedsOnboarding(true);
        setIsLoading(false);
        return;
      }

      // Check which workspace to show
      // 1. Try to use the last selected from localStorage
      const lastSelectedId = localStorage.getItem(`eagle_active_workspace_${user.id}`);
      let activeWs = lastSelectedId
        ? typedWorkspaces.find(w => w.id === lastSelectedId)
        : null;

      const hasIndividual = typedWorkspaces.some(w => w.plan_type === 'individual');
      const hasTeam = typedWorkspaces.some(w => w.plan_type === 'team');
      const lastSelectedValid = !!(lastSelectedId && typedWorkspaces.some(w => w.id === lastSelectedId));

      // 2. Só pede escolha de workspace quem tem os DOIS tipos de plano (individual + equipe)
      if (!activeWs && hasIndividual && hasTeam) {
        if (!lastSelectedValid) {
          setNeedsWorkspaceSelection(true);
          setNeedsOnboarding(false);
          setIsLoading(false);
          return;
        }
        activeWs = typedWorkspaces.find(w => w.id === lastSelectedId)!;
      } else if (!activeWs) {
        activeWs = lastSelectedValid
          ? typedWorkspaces.find(w => w.id === lastSelectedId)!
          : typedWorkspaces[0];
      }

      await selectWorkspace(activeWs);
    } catch (error) {
      console.error('Error loading workspace:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isOfflineMode, offlineSession?.userId]);

  // Persist último workspace visto para restaurar com sessão offline (sem JWT / user Supabase).
  useEffect(() => {
    if (!user?.id || !workspace) return;
    void setCachedData<OfflineWorkspaceBundle>(`workspace_bundle_${user.id}`, {
      workspace,
      allWorkspaces,
      members,
    });
  }, [user?.id, workspace, allWorkspaces, members]);

  const selectWorkspace = async (ws: Workspace) => {
    if (!user?.id) return;

    setWorkspace(ws);
    setNeedsOnboarding(false);
    setNeedsWorkspaceSelection(false);
    localStorage.setItem(`eagle_active_workspace_${user.id}`, ws.id);

    // Update profile with active workspace_id
    await supabase
      .from('profiles')
      .update({ workspace_id: ws.id })
      .eq('user_id', user.id);

    // Load members
    const { data: memberData } = await supabase
      .from('workspace_members')
      .select('*')
      .eq('workspace_id', ws.id);

    setMembers((memberData || []) as WorkspaceMember[]);
  };

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const ws = allWorkspaces.find(w => w.id === workspaceId);
    if (ws) {
      setIsLoading(true);
      await selectWorkspace(ws);
      setIsLoading(false);
    }
  }, [allWorkspaces, user?.id]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadWorkspace();
    } else if (!authLoading) {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, loadWorkspace]);

  const createWorkspace = useCallback(async (planType: PlanType): Promise<Workspace | null> => {
    if (!user?.id) return null;

    try {
      const maxUsers = planType === 'individual' ? 1 : 10;
      const name = planType === 'individual' ? 'Individual' : 'Equipe';
      
      const { data: ws, error: wsError } = await supabase
        .from('workspaces')
        .insert({
          name,
          owner_id: user.id,
          plan_type: planType,
          max_users: maxUsers,
        })
        .select()
        .single();

      if (wsError) throw wsError;

      const { error: memberError } = await supabase
        .from('workspace_members')
        .insert({
          workspace_id: ws.id,
          user_id: user.id,
          role: 'owner',
        });

      if (memberError) throw memberError;

      // Create default remuneration rules
      const defaultCategories = [
        {
          workspace_id: ws.id,
          name: 'Tabela SUS',
          category_type: 'procedure',
          discount_anesthetist: true,
          incentive_percent: 0,
          color: 'hsl(210, 75%, 50%)',
          notes: 'Valor SUS com desconto de 30% do anestesista, sem acréscimos.',
        },
        {
          workspace_id: ws.id,
          name: 'Urgência',
          category_type: 'procedure',
          discount_anesthetist: true,
          incentive_percent: 20,
          color: 'hsl(35, 90%, 50%)',
          notes: 'Valor com desconto de 30% do anestesista + 20% de acréscimo por urgência.',
        },
      ];

      await supabase.from('surgery_categories').insert(defaultCategories);

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          workspace_id: ws.id,
          onboarding_completed: true,
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      const newWorkspace = ws as Workspace;
      setWorkspace(newWorkspace);
      setAllWorkspaces(prev => [...prev, newWorkspace]);
      setNeedsOnboarding(false);
      setNeedsWorkspaceSelection(false);
      localStorage.setItem(`eagle_active_workspace_${user.id}`, ws.id);
      setMembers([{ id: '', workspace_id: ws.id, user_id: user.id, role: 'owner', created_at: new Date().toISOString() }]);

      return newWorkspace;
    } catch (error) {
      console.error('Error creating workspace:', error);
      return null;
    }
  }, [user?.id]);

  const inviteMember = useCallback(async (email: string): Promise<boolean> => {
    if (!workspace) return false;
    
    if (members.length >= workspace.max_users) {
      return false;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('user_id')
      .eq('email', email)
      .maybeSingle();

    if (!profile) return false;

    const { error } = await supabase
      .from('workspace_members')
      .insert({
        workspace_id: workspace.id,
        user_id: profile.user_id,
        role: 'member',
      });

    if (error) return false;

    await supabase
      .from('profiles')
      .update({ workspace_id: workspace.id, onboarding_completed: true })
      .eq('user_id', profile.user_id);

    await loadWorkspace();
    return true;
  }, [workspace, members, loadWorkspace]);

  const removeMember = useCallback(async (memberId: string): Promise<boolean> => {
    if (!workspace) return false;

    const { error } = await supabase
      .from('workspace_members')
      .delete()
      .eq('id', memberId);

    if (error) return false;

    await loadWorkspace();
    return true;
  }, [workspace, loadWorkspace]);

  const mergeWorkspaceStripeSync = useCallback((workspaceId: string, patch: WorkspaceStripeSyncPatch) => {
    setWorkspace((prev) => {
      if (!prev || prev.id !== workspaceId) return prev;
      return { ...prev, ...patch };
    });
    setAllWorkspaces((prev) =>
      prev.map((w) => (w.id === workspaceId ? { ...w, ...patch } : w))
    );
  }, []);

  const refreshWorkspaceById = useCallback(async (workspaceId: string) => {
    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('id', workspaceId)
      .maybeSingle();
    if (error || !data) return;
    const typed = data as Workspace;
    setAllWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? typed : w)));
    setWorkspace((prev) => (prev?.id === workspaceId ? typed : prev));
  }, []);

  // Check what the user can still create
  const hasIndividual = allWorkspaces.some(w => w.plan_type === 'individual');
  const hasTeam = allWorkspaces.some(w => w.plan_type === 'team');
  const canCreateIndividual = !hasIndividual;
  const canCreateTeam = !hasTeam;

  const isOwner = workspace?.owner_id === user?.id;
  const userMember = members.find(m => m.user_id === user?.id);
  const effectivePlanType = (workspace?.plan_type as PlanType) || 'individual';
  const isAdmin = effectivePlanType === 'individual' || isOwner || userMember?.role === 'admin';
  const isEditor = effectivePlanType === 'individual' || isAdmin || userMember?.role === 'editor';

  return (
    <WorkspaceContext.Provider value={{
      workspace,
      allWorkspaces,
      members,
      isOwner,
      isAdmin,
      isEditor,
      isLoading,
      needsOnboarding,
      needsWorkspaceSelection,
      planType: effectivePlanType,
      subscriptionStatus: workspace?.subscription_status || null,
      createWorkspace,
      switchWorkspace,
      inviteMember,
      removeMember,
      reloadWorkspace: loadWorkspace,
      mergeWorkspaceStripeSync,
      refreshWorkspaceById,
      canCreateIndividual,
      canCreateTeam,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
